import { Button } from "components/ui/button";
import { DownloadIcon } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

export default function TripsDownload() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasTrips, setHasTrips] = useState(false);
  const [trips, setTrips] = useState<any[]>([]);
  const [forceRefresh, setForceRefresh] = useState(0);
  const [sortDirection, setSortDirection] = useState("desc");

  const apiUrl = `http://${window.location.hostname}:3030`;

  useEffect(() => {
    fetch(apiUrl + "/trips/")
      .then((res) => res.json())
      .then(
        (result) => {
          setTrips(result);
          setHasTrips(result.length > 0);
        },
        (error) => {
          console.error("Error getting trip list:", error);
          setError(error.message);
        }
      )
      .finally(() => {
        setLoading(false);
      });
  }, [forceRefresh]);

  const formatUnixTimestampDate = (ts) => {
    return new Date(ts * 1000).toISOString().slice(0, 19).replace("T", " ");
  };

  const deleteTrip = (trip) => {
    const confirmText = t("file_download.delete_trip_confirmation", {
      0: formatUnixTimestampDate(trip["timestamp_start"]),
      1: formatUnixTimestampDate(trip["timestamp_end"]),
    });
    if (window.confirm(confirmText)) {
      fetch(apiUrl + "/trips/" + trip.trip_id + "/delete")
        .then((res) => res.json())
        .then(
          (result) => {
            console.info("Trip deleted:", result);
            setForceRefresh((prev: number) => prev + 1);
          },
          (error) => {
            console.error("Error deleting file:", error);
            alert("Error deleting file: " + error.message);
          }
        );
    }
  };

  const deleteAllTrip = () => {
    const confirmText = t("file_download.delete_all_trips_confirmation");
    if (window.confirm(confirmText)) {
      fetch(apiUrl + "/files/deleteall")
        .then((res) => res.json())
        .then(
          (result) => {
            console.info("All trips deleted:", result);
            setForceRefresh((prev: number) => prev + 1);
          },
          (error) => {
            console.error("Error deleting all trips:", error);
            alert("Error deleting all trips: " + error.message);
          }
        );
    }
  };

  const sortedTrips = useMemo(() => {
    return trips?.sort((a, b) => {
      const dateA = a.timestamp_start;
      const dateB = b.timestamp_start;
      return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
    });
  }, [trips, sortDirection]);

  if (loading) {
    return (
      <section>
        <p className="text-gray-400">{t("common.loading")}...</p>
      </section>
    );
  }

  if (error) {
    return (
      <section>
        <p className="font-medium text-rose-600">
          {t("file_download.error_message", { 0: error })}
        </p>
      </section>
    );
  }

  if (!loading && !hasTrips) {
    return (
      <section>
        <p className="text-gray-400">{t("file_download.no_data_message")}</p>
      </section>
    );
  }

  return (
    <section className="flex flex-col pb-20">
      <header className="space-y-3 mb-4 border border-primary-600 p-4 rounded-lg">
        <p>{t("file_download.description_trip_download")}</p>
        <div className="flex items-center space-x-2">
          <a href={apiUrl + "/trips/download/all"} tabIndex={-1}>
            <Button variant="secondary">
              {t("file_download.download_all_trips")}
            </Button>
          </a>
          <Button variant="destructive" onClick={() => deleteAllTrip()}>
            {t("file_download.delete_all_trips")}
          </Button>
        </div>
      </header>
      <div className="space-y-4 mb-2">
        <Button
          onClick={() => {
            setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
          }}
          variant="outline"
        >{`Sort by: ${sortDirection === "asc" ? "Oldest" : "Newest"}`}</Button>
        <p className="text-gray-400">
          {trips.length === 1
            ? t("file_download.trip_found", { 0: trips.length })
            : t("file_download.trips_found", { 0: trips.length })}
        </p>
      </div>
      <div className="divide-y divide-primary-600 border border-primary-600 rounded-lg">
        {sortedTrips.map((trip) => (
          <div key={trip["trip_id"]} className="p-1">
            <div className="flex items-center justify-between gap-4 px-3 py-2 transition-all hover:bg-primary-700 rounded-md">
              <div className="space-y-1">
                <p className="font-medium">
                  {t("file_download.trip_id")}: {trip["trip_id"]}
                </p>
                <p className="text-gray-400">
                  {formatUnixTimestampDate(trip["timestamp_start"])} -{" "}
                  {formatUnixTimestampDate(trip["timestamp_end"])}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <a
                  href={apiUrl + "/trips/" + trip["trip_id"] + "/download"}
                  tabIndex={-1}
                >
                  <Button variant="secondary">
                    <DownloadIcon className="w-4 h-4 mr-2" />
                    {t("file_download.download_trip")}
                  </Button>
                </a>
                <Button onClick={() => deleteTrip(trip)} variant="destructive">
                  {t("file_download.delete_trip")}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
